import { useRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { debounce, throttle } from "lodash-es";
import LinkWrap from "../LinkWrap/component";
import Icon from "../Icon";
import { metaNinePreviewReplacer } from "../../utils/page/library";
import {
    SCROLL_DISTANCE,
    BUTTON_OFFSET_WIDTH,
    SCROLL_DEBOUNCE_DELAY,
    RESIZE_THROTTLE_DELAY,
} from "./constants";

const InPageNav = ({ navigation }) => {
    const [horizontalScrollData, setHorizontalScrollData] = useState({
        nextButtonActive: "",
        previousButtonActive: "",
        scrollPosition: 0,
    });

    const hasResizeObserver = () => {
        return "ResizeObserver" in window;
    };

    const navRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [mouseMode, setMouseMode] = useState(false);

    useEffect(() => {
        navRef.current.scrollLeft = horizontalScrollData.scrollPosition;
        togglePrevNextButtons();
    }, [horizontalScrollData.scrollPosition]);

    useEffect(() => {
        if (!navRef.current) return;

        let resizeObserver;
        if (hasResizeObserver()) {
            resizeObserver = new ResizeObserver((entries) => {
                checkForNavOverflow(entries[0].target);
            });
            resizeObserver.observe(navRef.current);
        } else {
            window.addEventListener("resize", throttledResize);
        }
        const currentNavRef = navRef.current;
        return () => {
            if (hasResizeObserver()) {
                resizeObserver.unobserve(currentNavRef);
            } else {
                window.removeEventListener("resize", throttledResize);
            }
        };
    }, []);

    let history = useHistory();
    let location = useLocation();

    const checkForNavOverflow = (navElement) => {
        if (navElement.scrollWidth > navElement.offsetWidth) {
            togglePrevNextButtons();
        } else {
            setHorizontalScrollData({
                scrollPosition: 0,
                nextButtonActive: "",
                previousButtonActive: "",
            });
        }
    };

    const throttledResize = throttle(() => {
        checkForNavOverflow(navRef.current);
    }, RESIZE_THROTTLE_DELAY);

    const handleSelection = (e) => {
        e.preventDefault();

        if (!dropdownOpen && !mouseMode) return;
        setDropdownOpen(false);
        setMouseMode(false);

        if (!e.target && e.target.value) return;
        if (e.target.value === "") return;
        if (e.target.value === location.pathname) return;

        const externalUrl =
            e.target.options[e.target.selectedIndex].getAttribute(
                "data-external"
            );

        if (externalUrl === "true") {
            window.location.href = e.target.value;
            e.target.value = "";
        } else {
            history.push(e.target.value);
        }
    };

    const handleFocus = (e) => {
        if (navRef.current.scrollWidth > navRef.current.offsetWidth) {
            const targetPosition = e.target.offsetLeft - BUTTON_OFFSET_WIDTH;
            setHorizontalScrollData((prevState) => ({
                ...prevState,
                scrollPosition: targetPosition,
            }));
        }
    };

    const handleToggle = (e) => {
        // Allows for using tab key to navigate to next item in the nav
        if (e.keyCode === 9) {
            setDropdownOpen(false);
        } else {
            setDropdownOpen(true);
        }
    };

    const handleClick = () => {
        setDropdownOpen(true);
    };

    //safari specific select workaround since they don't trigger click events on select
    const handleMouseEnter = () => {
        setDropdownOpen(true);
        setMouseMode(true);
    };

    const handleUserScroll = () => {
        if (navRef.current.scrollWidth > navRef.current.offsetWidth) {
            setHorizontalScrollData((prevState) => ({
                ...prevState,
                scrollPosition: navRef.current.scrollLeft,
            }));
        }
    };

    const togglePrevNextButtons = () => {
        const activatePrevButton = horizontalScrollData.scrollPosition > 0;
        const activateNextButton =
            horizontalScrollData.scrollPosition <
            navRef.current.scrollWidth - navRef.current.offsetWidth;

        setHorizontalScrollData((prevState) => ({
            ...prevState,
            previousButtonActive: activatePrevButton ? "active" : "",
            nextButtonActive: activateNextButton ? "active" : "",
        }));
    };

    const showPreviousItems = () => {
        if (navRef.current.scrollLeft > 0) {
            setHorizontalScrollData((prevState) => ({
                ...prevState,
                scrollPosition: (horizontalScrollData.scrollPosition -=
                    SCROLL_DISTANCE),
            }));
        }
    };

    const showNextItems = () => {
        if (
            navRef.current.scrollLeft <
            navRef.current.scrollWidth - navRef.current.offsetWidth
        ) {
            setHorizontalScrollData((prevState) => ({
                ...prevState,
                scrollPosition: (horizontalScrollData.scrollPosition +=
                    SCROLL_DISTANCE),
            }));
        }
    };

    const renderDropdown = (data) => {
        const name = data.name
            ? data.name.replace(/\W/g, "").toLowerCase()
            : "More";
        const itemWithMatchingUrl = data.items.find(
            (item) => item.url === location.pathname
        );
        const labelText = itemWithMatchingUrl
            ? itemWithMatchingUrl.text
            : data.name || "More";
        return (
            <li
                key={name}
                className="in-page-nav-dropdown"
                data-testid="in-page-nav-dropdown"
            >
                <div className="dropdown-wrapper">
                    <label
                        htmlFor={`dropdown-select-${name}`}
                        className="dropdown-select-title"
                    >
                        {labelText}
                    </label>
                    <select
                        id={`dropdown-select-${name}`}
                        onBlur={handleSelection}
                        onChange={handleSelection}
                        onMouseEnter={handleMouseEnter}
                        onFocus={handleFocus}
                        onClick={handleClick}
                        onKeyUp={handleToggle}
                        onKeyDown={handleToggle}
                        value={
                            itemWithMatchingUrl ? itemWithMatchingUrl.url : ""
                        }
                        title={data.name || "More"}
                    >
                        {data.items.map((item) => {
                            return (
                                <option
                                    key={item.text}
                                    value={item.url}
                                    data-external={item.external}
                                >
                                    {item.text}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </li>
        );
    };

    const selectedPathName = location.pathname
        ? metaNinePreviewReplacer(location.pathname)
        : "";

    return (
        <div className="in-page-nav-wrapper">
            <div
                className={`previous-button-wrapper ${horizontalScrollData.previousButtonActive}`}
            >
                <button
                    className="previous-button"
                    aria-label="previous"
                    onClick={showPreviousItems}
                >
                    <Icon type="chevron" className="horizontal" />
                </button>
            </div>
            <nav
                className="in-page-nav"
                aria-label={navigation.name || ""}
                data-feature-name="navigation"
                data-feature-instance="in-page-nav"
                data-cy="in-page-nav"
                ref={navRef}
                onScroll={debounce(handleUserScroll, SCROLL_DEBOUNCE_DELAY)}
            >
                <ul>
                    {navigation.items.map((item) => {
                        if (item.type === "dropdown") {
                            return renderDropdown(item);
                        } else {
                            let url = item.url;
                            let hash = "";
                            if (item.url.includes("#")) {
                                const urlParts = item.url.split("#");
                                url = urlParts[0];
                                hash = "#" + urlParts[1];
                            }

                            return (
                                <li key={item.text}>
                                    <LinkWrap
                                        url={url}
                                        hash={hash}
                                        external={item.external}
                                        className={
                                            item.url === selectedPathName
                                                ? "selected"
                                                : ""
                                        }
                                        onFocus={handleFocus}
                                    >
                                        {item.text}
                                    </LinkWrap>
                                </li>
                            );
                        }
                    })}
                </ul>
            </nav>
            <div
                className={`next-button-wrapper ${horizontalScrollData.nextButtonActive}`}
            >
                <button
                    className="next-button"
                    aria-label="next"
                    onClick={showNextItems}
                >
                    <Icon type="chevron" className="horizontal" />
                </button>
            </div>
        </div>
    );
};
export default function InPageNavWrapper() {
    const navigation = useSelector((state) => {
        return state.navigation.inPage;
    });
    if (!navigation.hasOwnProperty("items") || navigation.items.length === 0)
        return null;
    return <InPageNav navigation={navigation} />;
}
